import padStart from 'lodash/padStart';

export const PERIOD_TYPE = {
	OVERTIME_ACTION:         'overtime_action',
	PENALTY_SHOOTOUT_ACTION: 'penalty_shootout_action',
	ACTION:                  'action',
	BREAK:                   'break',
};

export const CLOCK_START = 'clock_start';
export const CLOCK_STOP = 'clock_end';
export const CLOCK_SET = 'clock_set';

const THIRDTIME_START = 'third_start';
const THIRDTIME_END = 'third_end';
const ACTION_2M = '2_minutes';
const ACTION_5M = '5_minutes';
const OVERTIME_START = 'overtime_start';
const OVERTIME_END = 'overtime_end';
const PENALTY_SHOOTOUT_START = 'penalty_shootout_start';
const PENALTY_SHOOTOUT_END = 'penalty_shootout_end';

const thirdtimeTagNames = new Set([THIRDTIME_START, THIRDTIME_END]);
const penaltyShootoutTagNames = new Set([PENALTY_SHOOTOUT_START, PENALTY_SHOOTOUT_END]);
const overtimeTagNames = new Set([OVERTIME_START, OVERTIME_END]);
const clockTagNames = new Set([CLOCK_START, CLOCK_STOP, CLOCK_SET]);
const penaltyTagNames = new Set([ACTION_2M, ACTION_5M]);

const getPeriods = ({ event, tags }) => {
	const periodTags = tags.filter((item) =>
		thirdtimeTagNames.has(item.tag.name) ||
		overtimeTagNames.has(item.tag.name) ||
		penaltyShootoutTagNames.has(item.tag.name));
	periodTags.sort((a, b) => a.timestamp - b.timestamp);

	const tagPeriods = [];

	if (periodTags.length > 0) {
		if (periodTags[0].tag.name !== THIRDTIME_START &&
			periodTags[0].tag.name !== OVERTIME_START &&
			periodTags[0].tag.name !== PENALTY_SHOOTOUT_START) {
			console.log('Warning: Expected the first period tag to be a starting tag. Unable to determine game phases.');
		} else {
			for (let index = 0; index < periodTags.length; index++) {
				if (index > 0 && periodTags[index].tag.name === periodTags[index - 1].tag.name) {
					console.log('Warning: Unexpected error, encountered consecutive period tags of the same type.');
				}

				if (periodTags[index].tag.name === THIRDTIME_START ||
					periodTags[index].tag.name === OVERTIME_START ||
					periodTags[index].tag.name === PENALTY_SHOOTOUT_START) {
					if (index > 0) {
						tagPeriods.push({
							type:        PERIOD_TYPE.BREAK,
							start:       tagPeriods[tagPeriods.length - 1].end,
							end:         periodTags[index].timestamp,
							eventPeriod: event.periods?.length > tagPeriods.length ? event.periods[tagPeriods.length] : null,
						});
					}

					let type = PERIOD_TYPE.ACTION;
					if (periodTags[index].tag.name === OVERTIME_START) {
						type = PERIOD_TYPE.OVERTIME_ACTION;
					} else if (periodTags[index].tag.name === PENALTY_SHOOTOUT_START) {
						type = PERIOD_TYPE.PENALTY_SHOOTOUT_ACTION;
					}

					tagPeriods.push({
						type,
						start:       periodTags[index].timestamp,
						end:         Number.POSITIVE_INFINITY,
						eventPeriod: event.periods?.length > tagPeriods.length ? event.periods[tagPeriods.length] : null,
					});
				}

				if (periodTags[index].tag.name === THIRDTIME_END ||
					periodTags[index].tag.name === OVERTIME_END ||
					periodTags[index].tag.name === PENALTY_SHOOTOUT_END) {
					tagPeriods[tagPeriods.length - 1].end = periodTags[index].timestamp;
				}
			}
		}
	}

	if (tagPeriods.length === 0) {
		tagPeriods.push({
			type:        PERIOD_TYPE.BREAK,
			start:       0,
			end:         Number.POSITIVE_INFINITY,
			eventPeriod: event.periods?.length > 0 ? event.periods[0] : null,
		});
	}

	return tagPeriods;
};

const getPeriod = ({ position, event, tags, periods = null, additionalTimeAfterPeriodEnd = 0 }) => {
	const gamePeriods = Array.isArray(periods) ? periods : getPeriods({ event, tags });
	for (const period of gamePeriods) {
		if (period.start <= position && (period.end + additionalTimeAfterPeriodEnd) >= position) {
			return {
				...period,
				hasEnded: period.end < position,
			};
		}
	}
	return null;
};

const getGameTime = ({ position, event, tags, periods = null }) => {
	const currentPeriod = getPeriod({ position, event, tags, periods });
	if (!currentPeriod || currentPeriod.type === PERIOD_TYPE.BREAK) return { minutes: 0, seconds: 0 };

	const clockTags = tags.filter(
		(eventTag) =>
			clockTagNames.has(eventTag.tag.name) &&
			currentPeriod.start <= eventTag.timestamp &&
			eventTag.timestamp <= currentPeriod.end &&
			eventTag.timestamp < position
	);
	clockTags.sort((a, b) => a.timestamp - b.timestamp);

	let gameSeconds = 0;
	if (currentPeriod.type === PERIOD_TYPE.ACTION) {
		gameSeconds = (currentPeriod.eventPeriod?.startMinutes ?? 20) * 60;
	} else if (currentPeriod.type === PERIOD_TYPE.OVERTIME_ACTION) {
		gameSeconds = (currentPeriod.eventPeriod?.startMinutes ?? 5) * 60;
	}

	let lastClockStartPosition = currentPeriod.start;

	for (const clockTag of clockTags) {
		switch (clockTag.tag.name) {
		case CLOCK_START:
			lastClockStartPosition = clockTag.timestamp;
			break;
		case CLOCK_STOP:
			if (Number.isFinite(lastClockStartPosition)) {
				gameSeconds -= clockTag.timestamp - lastClockStartPosition;
			}
			lastClockStartPosition = null;
			break;
		case CLOCK_SET:
			gameSeconds = Number.parseInt(clockTag.value);
			if (lastClockStartPosition) lastClockStartPosition = clockTag.timestamp;
			break;
		default:
			throw new Error('Expected a clock tag.');
		}
	}
	if (lastClockStartPosition) gameSeconds -= position - lastClockStartPosition;

	if (gameSeconds <= 0) {
		return {
			minutes: 0,
			seconds: 0,
		};
	}

	return {
		minutes: Math.floor(gameSeconds / 60),
		seconds: gameSeconds % 60,
	};
};

const getPenalties = ({ position, event, tags, periods = null, homeTeamId, guestTeamId }) => {
	const currentPeriod = getPeriod({ position, event, tags, periods });
	if (!currentPeriod) return { [homeTeamId]: [], [guestTeamId]: [] };

	// Get breaks
	const breakTagsTime = getPeriods({ event, tags })
		.filter((period) =>
			period.type === PERIOD_TYPE.BREAK &&
			period.start <= currentPeriod.start
		);

	const clockTags = tags.filter(
		(eventTag) =>
			clockTagNames.has(eventTag.tag.name) &&
			eventTag.timestamp < position
	);
	clockTags.sort((a, b) => a.timestamp - b.timestamp);

	const penaltyTags = tags.filter(
		(eventTag) =>
			penaltyTagNames.has(eventTag.tag.name) &&
			eventTag.timestamp < position
	);
	penaltyTags.sort((a, b) => a.timestamp - b.timestamp);

	const penalties = { [homeTeamId]: [], [guestTeamId]: [] };
	const penaltySecondsArrays = {};

	penaltyTags.forEach(tag => {
		if (!penaltySecondsArrays[tag.teamId]) {
			penaltySecondsArrays[tag.teamId] = [];
		}

		let penaltySeconds = 0;
		if (tag.name == ACTION_2M) penaltySeconds = 120;
		else if (tag.name == ACTION_5M) penaltySeconds = 300;
		const duration = penaltySeconds;

		let penaltyPosition = tag.timestamp;

		// filter expired penalties
		let lastClockStopValue = null;
		for (const clockTag of clockTags) {
			if (clockTag.timestamp >= tag.timestamp) {
				switch (clockTag.tag.name) {
				case CLOCK_START:
					penaltyPosition = clockTag.timestamp;
					break;
				case CLOCK_STOP:
					if (Number.isFinite(penaltyPosition)) {
						penaltySeconds -= clockTag.timestamp - penaltyPosition;
					}
					lastClockStopValue = Number.parseInt(clockTag.value);
					penaltyPosition = null;
					break;
				case CLOCK_SET:
					penaltySeconds += Number.parseInt(clockTag.value) - lastClockStopValue;
					if (penaltyPosition) penaltyPosition = clockTag.timestamp;
					break;
				default:
					throw new Error('Expected a clock tag.');
				}
			}
		}

		// Add breaks
		let breaksWithoutCurrent = breakTagsTime;
		if (currentPeriod.type === PERIOD_TYPE.BREAK) breaksWithoutCurrent = breaksWithoutCurrent.slice(0, -1);
		penaltySeconds += breaksWithoutCurrent
			.filter((period) => period.start >= tag.timestamp)
			.reduce((acc, period) => acc + (period.end - period.start), 0);

		if (penaltyPosition) {
			if (currentPeriod.type === PERIOD_TYPE.BREAK) {
				penaltySeconds -= currentPeriod.start - penaltyPosition;
			} else {
				// Ignore 3rd penaly
				if (penalties[tag.teamId].length <= 1) {
					penaltySeconds -= position - penaltyPosition;
				}
			}
		}

		const penaltySecondsArr = penaltySecondsArrays[tag.teamId];
		penaltySecondsArr.push({ penaltySeconds, duration });

		// Handle 3rd penalty
		if (penaltySecondsArr.length > 2) {
			const firstPenalty = penaltySecondsArr[penaltySecondsArr.length - 3];
			const secondPenalty = penaltySecondsArr[penaltySecondsArr.length - 2];
			const smallestPenalty = firstPenalty.penaltySeconds < secondPenalty.penaltySeconds ? firstPenalty : secondPenalty;
			const originalPenaltySeconds = tag.name == ACTION_2M ? 120 : 300;
			if (smallestPenalty.penaltySeconds < 0) {
				// Check if past penalties influence the last
				if (-smallestPenalty.penaltySeconds < duration - penaltySeconds) {
					penaltySeconds = originalPenaltySeconds + smallestPenalty.penaltySeconds;
				}
			}
			else penaltySeconds = originalPenaltySeconds;
		}

		if (penaltySeconds > 0) {
			const minutes = penaltySeconds / 60;
			const seconds = penaltySeconds % 60;

			const formattedTime = `${padStart(Math.floor(minutes), 2, '0')}:${padStart(Math.floor(seconds), 2, '0')}`;
			penalties[tag.teamId].push(formattedTime);
		}
	});

	Object.keys(penalties).forEach(key => {
		penalties[key] = penalties[key].slice(0, 3);
	});

	return penalties;
};

export { getGameTime, getPeriods, getPeriod, getPenalties };
